import type { ReactNode } from "react";
import { ButtonStyleType, CancelLabelType, ColorType, ConfirmLabelType } from "@/types/shared/uiComponents";
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from "@mui/material";
import Button from "@/components/ui/Button";

interface ConfirmationDialogProps {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: ConfirmLabelType;
  cancelLabel?: CancelLabelType;
  confirmColor?: ColorType;
  onConfirm: () => void;
  onCancel: () => void;
  /** Extra content below the message (e.g. a list or an option checkbox). */
  children?: ReactNode;
  /**
   * The confirmed action is running: the confirm button shows a spinner and both
   * buttons (and Escape / backdrop) are off, so it cannot be sent twice.
   * Optional and false by default, so existing callers are unchanged.
   */
  isLoading?: boolean;
  /** The confirm button is off (e.g. the action is no longer possible); Cancel stays. Optional, false by default. */
  confirmDisabled?: boolean;
}

export const ConfirmationDialog = ({
  open,
  title,
  message,
  confirmLabel = ConfirmLabelType.Confirm,
  cancelLabel = CancelLabelType.Cancel,
  confirmColor = ColorType.Primary,
  onConfirm,
  onCancel,
  children,
  isLoading = false,
  confirmDisabled = false,
}: ConfirmationDialogProps) => {
  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!isLoading) onCancel();
      }}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle variant="h3">{title}</DialogTitle>
      <DialogContent>
        <DialogContentText>{message}</DialogContentText>
        {children}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} color={ColorType.Inherit} variant={ButtonStyleType.Text} disabled={isLoading}>
          {cancelLabel}
        </Button>
        <Button
          onClick={onConfirm}
          // MUI buttons have no "default" colour; it reads as the neutral one.
          color={confirmColor === ColorType.Default ? ColorType.Inherit : confirmColor}
          variant={ButtonStyleType.Contained}
          loading={isLoading}
          disabled={isLoading || confirmDisabled}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
